import { Button, Form, Upload } from "antd";
import { SvgIcon } from "assets/icons";
import Img from "assets/images";
import FormInput from "components/FormInput";
import PageHeader from "components/PageHeader";
import Spinner from "components/Spinner";
import { ACCEPT_IMG_TYPES, FORM_LAYOUT, FORM_TYPE, INPUT_TYPE } from "config/constants";
import { useGetBranchQuery } from "hooks/branch";
import { useGetCategoriesLevel1 } from "hooks/category/category";
import { useCities, useWards } from "hooks/common";
import { useDepartments } from "hooks/department/department";
import { useGetRolesWithPermissionCodes } from "hooks/role";
import { useUpload } from "hooks/upload";
import { useCreateUser, useGetUser, useGetUserPermissions, useStaffPermissions, useUpdateUser } from "hooks/user/user";
import { useGetSeller } from "hooks/seller";
import { useWarehouse } from "hooks/warehouse";
import { t } from "i18next";
import Page403 from "pages/PageError/403";
import { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  debounce,
  dummyRequest,
  notify,
  passwordGenerator,
  readFile,
  validateFile,
  validator,
} from "utils/helperFuncs";
import { omit } from "utils/object";
import { hasPermissionDataUser, USER_TYPE, USER_TYPE_OPTIONS } from "../constants";
import "./index.scss";

const ACCOUNT_LIST_PATH = "/account";
const MEDIUM_INPUT_LENGTH = 255;
const DEBOUNCE_TIMEOUT = 1000;

//data must be { id:"", name:"" } array
const convertOptions = (data) => {
  return data
    ? data.map(({ id, name, permissions }) => ({
        label: name,
        value: id,
        permissions: permissions,
      }))
    : [];
};
export const CreateAccountForm = ({ type = FORM_TYPE.CREATE }) => {
  const { id } = useParams();
  const { user } = useGetUser({ id });

  const [avatar, setAvatar] = useState({
    base64String: "",
    src: "",
  });
  const [location, setLocation] = useState({
    cityID: null,
  });

  const [uploading, setUploading] = useState(false);
  const [departmentSearchText, setDepartmentSearchText] = useState("");
  const [roleSearchText, setRoleSearchText] = useState("");
  const [permissionsSelected, setPermissionsSelected] = useState([]);
  const [isProductAll, setIsProductAll] = useState(false);
  const [rolesIdArray, setRolesIdArray] = useState([]);
  const [userType, setUserType] = useState(USER_TYPE.STAFF);
  const [sellerSearchText, setSellerSearchText] = useState("");
  const isPartnerStaff = userType === USER_TYPE.PARTNER_STAFF;

  const [form] = Form.useForm();
  const { handleUpload } = useUpload();
  const history = useHistory();

  const values = form?.getFieldsValue();

  const permissionProductAll = hasPermissionDataUser(user, "product_all");

  const permissionProductAllClient = permissionsSelected
    ?.flatMap((item) => item?.map((subItem) => subItem.code))
    ?.includes("product_all");

  const { cities } = useCities();
  const { wards, refetch: refetchWards } = useWards(location?.cityID);
  const { roles, loading: loadingRoles } = useGetRolesWithPermissionCodes({
    offset: 0,
    query: roleSearchText || undefined,
  });

  const { data: dataAllCategories } = useGetCategoriesLevel1();

  const { data: dataBranch } = useGetBranchQuery();
  const { handleCreateUser } = useCreateUser();
  const { handleUpdateUser } = useUpdateUser();
  const { warehouseOptions, loading: loadingWarehouses } = useWarehouse();
  const { data: sellersForPartner, loading: loadingSellersForPartner } = useGetSeller({
    filters: { query: sellerSearchText || undefined },
    pagination: { offset: 0, limit: 50 },
  });
  const sellerOptionsForPartner = sellersForPartner?.map(({ id, code, fullName }) => ({
    label: code ? `${code} - ${fullName}` : fullName,
    value: id,
  }));
  // The account's currently assigned seller may not be among the (paginated/searched)
  // options above, in which case antd's Select would render the raw ID instead of a
  // friendly label — make sure it's always present as an option.
  const currentSellerOption =
    user?.sellerID && user?.seller?.fullName ? { label: user.seller.fullName, value: user.sellerID } : null;
  const sellerOptionsWithCurrent =
    currentSellerOption && !sellerOptionsForPartner?.some((option) => option.value === currentSellerOption.value)
      ? [currentSellerOption, ...(sellerOptionsForPartner || [])]
      : sellerOptionsForPartner;

  const { departments = [], loading: loadingDepartments } = useDepartments({
    filters: {
      query: departmentSearchText || undefined,
    },
    pagination: {
      offset: 0,
      limit: 100,
    },
  });

  const password = type === FORM_TYPE.CREATE ? passwordGenerator(8) : "********";

  const handleValuesChange = (changedValues, allValues) => {
    if (changedValues?.roleIDs) {
      setRolesIdArray(changedValues?.roleIDs);
    }
    if (changedValues?.userType) {
      setUserType(changedValues.userType);
      if (changedValues.userType !== USER_TYPE.PARTNER_STAFF) {
        form.setFieldsValue({ partnerID: undefined, warehouseIDs: undefined });
      }
    }
  };

  const handleSearchDepartment = ([text]) => {
    if (text[0] === " ") {
      return;
    }
    const inputText = text.length > 0 ? text : null;
    setDepartmentSearchText(inputText);
  };

  const handleDebouncedSearchDepartment = debounce(handleSearchDepartment, DEBOUNCE_TIMEOUT);

  const handleSearchRole = ([text]) => {
    if (text[0] === " ") {
      return;
    }
    const inputText = text.length > 0 ? text : null;
    setRoleSearchText(inputText);
  };

  const handleDebouncedSearchRole = debounce(handleSearchRole, DEBOUNCE_TIMEOUT);

  const handleSearchSeller = ([text]) => {
    if (text[0] === " ") {
      return;
    }
    const inputText = text.length > 0 ? text : null;
    setSellerSearchText(inputText);
  };

  const handleDebouncedSearchSeller = debounce(handleSearchSeller, DEBOUNCE_TIMEOUT);

  const validateWhitespace = (_, value) => {
    if (value && value.trim() === "") {
      return Promise.reject(t("account.noSpace"));
    } else {
      return Promise.resolve();
    }
  };

  const handleSearchSelect = (search, option) => {
    return option?.label?.toLowerCase()?.includes(search?.toLowerCase());
  };

  const formFields = [
    {
      formItemOptions: {
        label: <b>{t("account.name")}</b>,
        name: "fullname",
        rules: [{ required: true, message: t("account.pleaseEnterName") }, { validator: validateWhitespace }],
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.enterName"),
        maxLength: 255,
      },
    },
    {
      formItemOptions: {
        label: <b>{t("account.code")}</b>,
        // name: 'code',
        rules: [{ required: true, message: t("account.pleaseEnterCode") }],
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.enterCode"),
      },
    },
    {
      formItemOptions: {
        label: <b>Email</b>,
        name: "email",
        rules: [
          { required: true, message: t("account.pleaseWenterEmail") },
          validator({
            type: "email",
          }),
        ],
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.enterEmailForLogin"),
      },
    },
    {
      formItemOptions: {
        label: <b>{t("account.phoneNumber")}</b>,
        name: "telephone",
        rules: [
          { required: false, message: t("account.pleaseEnterPhoneNumber") },
          validator({
            type: "phone",
          }),
        ],
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.enterMax"),
        maxLength: 10,
      },
    },
    {
      formItemOptions: {
        label: <b>{t("account.password")}</b>,
        name: "raw_password",
        initialValue: password,
        rules: [
          { required: true, message: t("account.pleaseEnterPassword") },
          type === FORM_TYPE.CREATE &&
            validator({
              type: "password",
            }),
        ],
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.enterPassword"),
        maxLength: 50,
        disabled: type === FORM_TYPE.DETAIL,
      },
    },
    {
      type: INPUT_TYPE.SELECT,
      formItemOptions: {
        label: <b>{t("account.userType")}</b>,
        name: "userType",
        initialValue: USER_TYPE.STAFF,
        rules: [{ required: true, message: t("account.pleaseSelectUserType") }],
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.selectUserType"),
        options: USER_TYPE_OPTIONS,
      },
    },
    {
      type: INPUT_TYPE.SELECT,
      formItemOptions: {
        label: <b>{t("account.province")}</b>,
        name: "cityID",
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.enterProvince"),
        options: cities?.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
        onChange: handleChangeCity,
      },
    },
    {
      type: INPUT_TYPE.SELECT,
      formItemOptions: {
        label: <b>{t("account.wards")}</b>,
        name: "wardID",
      },
      inputOptions: {
        size: "large",
        placeholder: t("account.pleaseSelectWards"),
        options: wards?.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
      },
    },
    {
      formItemOptions: {
        label: <b>{t("account.address")}</b>,
        name: "address",
        className: "address",
      },
      inputOptions: {
        placeholder: t("account.enterAdress"),
        maxLength: MEDIUM_INPUT_LENGTH,
        showCount: true,
        size: "large",
      },
    },
    {
      type: INPUT_TYPE.SELECT,
      formItemOptions: {
        label: <b>{t("account.branch")}</b>,
        name: "branchIDs",
        rules: [{ required: true, message: t("account.messageSlectBranch") }],
      },
      inputOptions: {
        placeholder: t("account.selectBranch"),
        mode: "multiple",
        allowClear: true,
        showSearch: true,
        optionFilterProp: "label",
        placement: "topLeft",
        loading: loadingRoles,
        size: "large",
        filterOption: (search, option) => handleSearchSelect(search, option),

        onBlur: () => {
          setRoleSearchText("");
        },
        options: dataBranch,
      },
    },
    {
      type: INPUT_TYPE.SELECT,
      formItemOptions: {
        label: <b>{t("account.department")}</b>,
        name: "departmentID",
        rules: [{ required: true, message: t("account.enterDepartment") }],
      },
      inputOptions: {
        placeholder: t("account.selectDepartment"),
        allowClear: true,
        showSearch: true,
        filterOption: false,
        optionFilterProp: "label",
        placement: "topLeft",
        size: "large",
        loading: loadingDepartments,
        onSearch: handleDebouncedSearchDepartment,
        onFocus: () => {
          setDepartmentSearchText("");
        },
        options: convertOptions(departments),
      },
    },
    {
      type: INPUT_TYPE.SELECT,
      formItemOptions: {
        label: <b>{t("account.roles")}</b>,
        name: "roleIDs",
        rules: [{ required: true, message: t("account.pleaseSelectRoles") }],
      },
      inputOptions: {
        placeholder: t("account.selectRoles"),
        mode: "multiple",
        allowClear: true,
        showSearch: true,
        filterOption: false,
        optionFilterProp: "label",
        placement: "topLeft",
        loading: loadingRoles,
        size: "large",
        onSearch: handleDebouncedSearchRole,
        onBlur: () => {
          setRoleSearchText("");
        },
        onChange: (value, option) => setPermissionsSelected(option?.map(({ permissions }) => permissions)),
        options: convertOptions(roles),
      },
    },
  ];

  function handleChangeCity(cityID) {
    form.setFieldsValue({
      wardID: null,
    });
    setLocation({
      ...location,
      cityID,
    });
  }

  function onUploadChange(info) {
    if (info.file.status === "uploading") {
      setUploading(true);
    }
    if (info.file.status === "done") {
      if (!ACCEPT_IMG_TYPES.includes(info?.file?.type)) {
        notify.error({
          message: t("account.conditionImage"),
        });
        setUploading(false);
        return;
      }
      setUploading(false);
      readFile({
        getResult: async (result) => {
          const uploadUrl = await handleUpload({ file: info?.file?.originFileObj });
          setAvatar({
            base64String: result,
            src: uploadUrl,
          });
        },
        type: "Base64String",
        file: info?.file?.originFileObj,
      });
    } else if (info.file.status === "error") {
      return;
    }
  }

  function buildPartnerFields(values) {
    const { partnerID, warehouseIDs, ...rest } = values;
    if (rest.userType !== USER_TYPE.PARTNER_STAFF) {
      return rest;
    }
    return {
      ...rest,
      partnerStaffInput: { partnerID, warehouseIDs },
    };
  }

  async function createUser(values) {
    try {
      await handleCreateUser({
        request: {
          avatarURL: avatar?.src,
          ...buildPartnerFields(values),
        },
      });
      notify.success({
        message: t("account.createAccountSuccess"),
      });
      history.push({
        pathname: ACCOUNT_LIST_PATH,
      });
    } catch (err) {
      notify.error({
        message: t("account.createAccountFaild"),
        description: err.message,
      });
    }
  }

  function initForm() {
    form.setFieldsValue({
      ...(user || {}),
      roleIDs: user?.roles?.map(({ id }) => id),
      categoryIDs: user?.categories?.map(({ id }) => id),
      branchIDs: user?.branches?.map(({ id }) => id),
      warehouseIDs: user?.warehouses?.map(({ id }) => id),
      partnerID: user?.sellerID,
      userType: user?.type,
    });
    setUserType(user?.type || USER_TYPE.STAFF);
  }

  async function updateUser(values) {
    try {
      // does not support update password
      values = omit(values, "raw_password");
      await handleUpdateUser({
        request: {
          id,
          avatarURL: avatar.src,
          categoryIDs: isProductAll ? values?.categoryIDs : [],
          ...buildPartnerFields(values),
        },
      });
      notify.success({
        message: t("account.updateAccountSuccess"),
      });
      history.push({
        pathname: ACCOUNT_LIST_PATH,
      });
    } catch (err) {
      notify.error({
        message: t("account.updateAccountFaild"),
        description: err.message,
      });
    }
  }

  useEffect(() => {
    type === FORM_TYPE.DETAIL && initForm();
    setLocation({
      cityID: user?.cityID,
    });
    // set avatar url
    setAvatar({
      src: user?.avatarURL,
    });
    setIsProductAll(permissionProductAll);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (location?.cityID) {
      refetchWards(location?.cityID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    setPermissionsSelected([...permissionsSelected]);
    setIsProductAll(permissionProductAllClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsSelected?.length]);

  useEffect(() => {
    // type === FORM_TYPE.CREATE &&
    form.setFieldsValue({
      ...(user || {}),
      categoryIDs: isProductAll ? user?.categories?.map(({ id }) => id) : [],
    });
  }, [isProductAll]);

  return (
    <div className="create-account-container">
      <PageHeader
        pageTitle={type === FORM_TYPE.DETAIL ? t("account.infoAccount") : t("account.createAccount")}
        routes={[
          {
            path: "/setting",
            name: t("account.setup"),
          },
          {
            path: "/account",
            name: t("account.accountManagament"),
          },
          {
            path: "",
            name: type === FORM_TYPE.DETAIL ? t("account.infoAccount") : t("account.createAccount"),
          },
        ]}
      />

      <Form
        onFinish={type === FORM_TYPE.DETAIL ? updateUser : createUser}
        form={form}
        {...FORM_LAYOUT}
        onValuesChange={handleValuesChange}
      >
        <div className="form-content">
          <div className="avatar-upload">
            <p className="title">{t("account.avatar")}</p>
            <div className="avatar">
              <img alt="avatar" src={avatar?.base64String || avatar?.src || Img.AvatarPlaceHolder} />
            </div>

            <div className="upload">
              <Upload
                loading={uploading}
                showUploadList={false}
                onChange={onUploadChange}
                customRequest={({ file, onSuccess }) => dummyRequest(file, onSuccess)}
                beforeUpload={(file) => {
                  if (!validateFile(file)) {
                    notify.error({
                      message: t("uploadDocument.limitErrorMessage"),
                      description: file?.name,
                    });
                    return Upload.LIST_IGNORE;
                  }
                  return validateFile(file);
                }}
              >
                {!avatar?.base64String && !avatar?.src ? (
                  <div className="icon_upload">
                    <SvgIcon.IconUploadImage />
                    <span> {t("account.uploadImage")}</span>
                  </div>
                ) : (
                  <div className="icon_upload"> </div>
                )}
              </Upload>
              <p className="title-rules">
                <span>{t("account.conditionAvatar")}</span>
                <div>{t("account.maxFiveMB")}</div>
              </p>
            </div>
          </div>
          <div className="common-info">
            <div className="form-fields">
              {formFields.map((field, index) => {
                return <FormInput key={index} {...field} />;
              })}
              {values?.roleIDs?.length && isProductAll ? (
                <FormInput
                  {...{
                    type: INPUT_TYPE.SELECT,
                    formItemOptions: {
                      label: <b>{t("campaign.form.category")}</b>,
                      name: "categoryIDs",
                      rules: [{ required: true, message: t("account.pleaseSelectCategories") }],
                    },
                    inputOptions: {
                      placeholder: t("campaign.form.categoryPlaceholder"),
                      mode: "multiple",
                      allowClear: true,
                      showSearch: true,
                      optionFilterProp: "label",
                      placement: "topLeft",
                      loading: loadingRoles,
                      size: "large",
                      filterOption: (search, option) => handleSearchSelect(search, option),
                      onBlur: () => {
                        setRoleSearchText("");
                      },
                      options: convertOptions(dataAllCategories),
                    },
                  }}
                />
              ) : (
                <></>
              )}
              {isPartnerStaff && (
                <>
                  <FormInput
                    type={INPUT_TYPE.SELECT}
                    formItemOptions={{
                      label: <b>{t("account.partner")}</b>,
                      name: "partnerID",
                      rules: [{ required: true, message: t("account.pleaseSelectPartner") }],
                    }}
                    inputOptions={{
                      placeholder: t("account.selectPartner"),
                      allowClear: true,
                      showSearch: true,
                      filterOption: false,
                      optionFilterProp: "label",
                      placement: "topLeft",
                      loading: loadingSellersForPartner,
                      size: "large",
                      onSearch: handleDebouncedSearchSeller,
                      onBlur: () => {
                        setSellerSearchText("");
                      },
                      options: sellerOptionsWithCurrent,
                    }}
                  />
                  <FormInput
                    type={INPUT_TYPE.SELECT}
                    formItemOptions={{
                      label: <b>{t("account.exportWarehouseIDs")}</b>,
                      name: "warehouseIDs",
                      rules: [{ required: true, message: t("account.pleaseSelectExportWarehouseIDs") }],
                    }}
                    inputOptions={{
                      placeholder: t("account.selectExportWarehouseIDs"),
                      mode: "multiple",
                      allowClear: true,
                      showSearch: true,
                      optionFilterProp: "label",
                      placement: "topLeft",
                      loading: loadingWarehouses,
                      size: "large",
                      filterOption: (search, option) => handleSearchSelect(search, option),
                      options: warehouseOptions,
                    }}
                  />
                </>
              )}
            </div>
            <div className="button-group">
              <Link
                to={{
                  pathname: "/account",
                  state: {
                    tab: "account-list",
                  },
                }}
              >
                <Button className="cancel-btn custom-button">{t("account.cancel")}</Button>
              </Link>
              <Form.Item>
                <Button className="custom-button" type="primary" htmlType="submit">
                  {t("account.save")}
                </Button>
              </Form.Item>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

const CreateAccount = () => {
  const { loading: loadUserPermissions } = useGetUserPermissions();
  const { canCreate } = useStaffPermissions();

  return loadUserPermissions ? (
    <Spinner loading={loadUserPermissions} />
  ) : canCreate ? (
    <CreateAccountForm type={FORM_TYPE.CREATE} />
  ) : (
    <Page403 />
  );
};

export default CreateAccount;
